/**
 * Election and race stage vocabulary, shared with mobile
 * (`votemate-mobile-app/src/utils/electionType.ts`) and the
 * backend (`votemate-backend/utils/election_type.py`). Keep the three in step.
 *
 * Democracy Works names each election in free text, and one election day can
 * hold more than one stage: Louisiana's November ballot is the U.S. Senate
 * general plus the open primary for every other race. The election type comes
 * from that name; a race's own stage comes from Ballotpedia (`contest.stage`).
 */

export type ElectionType =
  | 'general'
  | 'generalPrimary'
  | 'generalRunoff'
  | 'primary'
  | 'primaryRunoff'
  | 'special'
  | 'other';

export type RaceStage = 'general' | 'primary' | 'primaryRunoff' | 'generalRunoff';

const GENERAL_AND_PRIMARY = /\bgeneral\s+(?:and|&)\s+primary\b|\bprimary\s+(?:and|&)\s+general\b/;

/**
 * Read a Democracy Works election name into an election type. Order matters,
 * because names stack words:
 *
 * - `recall` → special.
 * - `runoff` → primary runoff when the name says primary (Georgia's "General
 *   Primary Runoff"), else general runoff; a runoff naming neither is special.
 * - `open general` → general runoff: Louisiana names its December runoff
 *   "Open General", never "runoff".
 * - "general and primary" / "primary and general" → general & primary.
 * - then `primary` (Georgia's spring "General Primary" included), `general`,
 *   `special`.
 */
export const classifyElectionType = (description: string | null | undefined): ElectionType => {
  const d = (description ?? '').toLowerCase();
  if (d.includes('recall')) return 'special';
  if (d.includes('runoff')) {
    if (d.includes('primary')) return 'primaryRunoff';
    if (d.includes('general')) return 'generalRunoff';
    return 'special';
  }
  if (d.includes('open general')) return 'generalRunoff';
  if (GENERAL_AND_PRIMARY.test(d)) return 'generalPrimary';
  if (d.includes('primary')) return 'primary';
  if (d.includes('general')) return 'general';
  if (d.includes('special')) return 'special';
  return 'other';
};

/** Types that belong on the national ballot's General tab. */
export const isGeneralElectionType = (type: string | null | undefined): boolean =>
  type === 'general' || type === 'generalPrimary' || type === 'generalRunoff';

export const isPrimaryElectionType = (type: string | null | undefined): boolean =>
  type === 'primary' || type === 'primaryRunoff';

/** `tabs.*` key for an election tab ("General & Primary Election"). */
export const ELECTION_TAB_LABEL_KEYS: Record<ElectionType, string> = {
  general: 'tabs.generalElection',
  generalPrimary: 'tabs.generalPrimaryElection',
  generalRunoff: 'tabs.generalRunoffElection',
  primary: 'tabs.primaryElection',
  primaryRunoff: 'tabs.primaryRunoffElection',
  special: 'tabs.specialElection',
  other: 'tabs.election',
};

/** `elections.type*` key for a national card title; `other` falls back to the name. */
export const ELECTION_TYPE_LABEL_KEYS: Partial<Record<ElectionType, string>> = {
  general: 'elections.typeGeneral',
  generalPrimary: 'elections.typeGeneralPrimary',
  generalRunoff: 'elections.typeGeneralRunoff',
  primary: 'elections.typePrimary',
  primaryRunoff: 'elections.typePrimaryRunoff',
  special: 'elections.typeSpecial',
};

/** `election_types` values on "Sample Ballot Tabs Available" (both clients). */
export const ELECTION_TYPE_ANALYTICS_KEYS: Record<ElectionType, string> = {
  general: 'general',
  generalPrimary: 'general_primary',
  generalRunoff: 'general_runoff',
  primary: 'primary',
  primaryRunoff: 'primary_runoff',
  special: 'special',
  other: 'other',
};

/** `election_type` values on "Sample Ballot Tab Selected" (both clients). */
export const ELECTION_TYPE_ANALYTICS_LABELS: Record<ElectionType, string> = {
  general: 'General',
  generalPrimary: 'General & Primary',
  generalRunoff: 'General Runoff',
  primary: 'Primary',
  primaryRunoff: 'Primary Runoff',
  special: 'Special',
  other: 'Other',
};

const RACE_STAGE_LABEL_KEYS: Record<RaceStage, string> = {
  general: 'elections.stageGeneral',
  primary: 'elections.stagePrimary',
  primaryRunoff: 'elections.stagePrimaryRunoff',
  generalRunoff: 'elections.stageGeneralRunoff',
};

// The stage every race on an election of this type has, when there is one.
const EXPECTED_STAGE: Partial<Record<ElectionType, RaceStage>> = {
  general: 'general',
  generalRunoff: 'generalRunoff',
  primary: 'primary',
  primaryRunoff: 'primaryRunoff',
};

/**
 * The `elections.stage*` key to tag a race with, or null when the election's
 * own label already says the race's stage. Tags every race on a mixed day
 * (General & Primary) and any race whose stage differs from its election's,
 * such as Georgia's nonpartisan judicial generals on primary day.
 */
export const raceStageLabelKey = (
  stage: string | null | undefined,
  electionType: ElectionType | undefined
): string | null => {
  if (!stage || !Object.prototype.hasOwnProperty.call(RACE_STAGE_LABEL_KEYS, stage)) return null;
  if (electionType && EXPECTED_STAGE[electionType] === stage) return null;
  return RACE_STAGE_LABEL_KEYS[stage as RaceStage];
};
